import { SnapArea } from 'shared/types'

import { WINDOW_DRAG_CONSTANTS } from '../constants'
import type { SnapRect } from '../types/windowDrag'

export function isSnapArea(area: SnapArea | null): boolean {
  if (area === null) return false
  return area !== SnapArea.FULLSCREEN
}

export function getViewportDimensions() {
  return {
    width: globalThis.window.innerWidth,
    height: globalThis.window.innerHeight
  }
}

export function isPointInRect(
  point: { x: number; y: number },
  rect: { x: number; y: number; width: number; height: number }
): boolean {
  return (
    point.x >= rect.x &&
    point.x <= rect.x + rect.width &&
    point.y >= rect.y &&
    point.y <= rect.y + rect.height
  )
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max))
}

export function calculateDesiredPositionFromMaximized(
  clientX: number,
  clientY: number,
  windowSize: { width: number; height: number },
  topOffset: number
): { x: number; y: number } {
  const { width: viewportWidth, height: viewportHeight } =
    getViewportDimensions()
  const maxX = viewportWidth - windowSize.width
  const maxY = viewportHeight - windowSize.height

  // Calculate the relative cursor position within the maximized window (0-1 ratio)
  // This maintains the cursor's relative position when transitioning from maximized to normal
  const ratioX = clamp(clientX / viewportWidth, 0, 1)

  // Position the window so the cursor stays at the same relative position
  // e.g., if cursor was at 25% of maximized window, it should be at 25% of unmaximized window
  const desiredX = clientX - ratioX * windowSize.width

  return {
    x: clamp(desiredX, 0, maxX),
    y: clamp(
      clientY - WINDOW_DRAG_CONSTANTS.TITLE_BAR_HEIGHT / 2,
      topOffset,
      maxY
    )
  }
}

export function restartDragControls(
  controls: {
    stop: () => void
    cancel: () => void
    start: (event: PointerEvent) => void
  },
  event: PointerEvent,
  delay: number
): void {
  controls.stop()
  controls.cancel()
  setTimeout(() => {
    controls.start(event)
  }, delay)
}

export function convertSnapRectWidthToNumber(width: string | number): number {
  if (typeof width === 'string') {
    return Math.floor((parseFloat(width) / 100) * globalThis.window.innerWidth)
  }
  return width
}

export function calculateSnapRect(area: SnapArea, topOffset: number): SnapRect {
  const { width: viewportWidth, height: viewportHeight } =
    getViewportDimensions()
  const workAreaHeight = viewportHeight - topOffset
  const halfWidth = Math.floor(viewportWidth / 2)
  const halfHeight = Math.floor(workAreaHeight / 2)

  switch (area) {
    case SnapArea.FULLSCREEN:
      return {
        x: 0,
        y: topOffset,
        width: viewportWidth,
        height: workAreaHeight
      }
    case SnapArea.LEFT_HALF:
      return {
        x: 0,
        y: topOffset,
        width: '50%',
        height: workAreaHeight
      }
    case SnapArea.RIGHT_HALF:
      return {
        x: viewportWidth - halfWidth,
        y: topOffset,
        width: '50%',
        height: workAreaHeight
      }
    case SnapArea.TOP_LEFT:
      return {
        x: 0,
        y: topOffset,
        width: '50%',
        height: halfHeight
      }
    case SnapArea.BOTTOM_LEFT:
      return {
        x: 0,
        y: topOffset + halfHeight,
        width: '50%',
        height: halfHeight
      }
    case SnapArea.TOP_RIGHT:
      return {
        x: viewportWidth - halfWidth,
        y: topOffset,
        width: '50%',
        height: halfHeight
      }
    case SnapArea.BOTTOM_RIGHT:
      return {
        x: viewportWidth - halfWidth,
        y: topOffset + halfHeight,
        width: '50%',
        height: halfHeight
      }
    default: {
      const _exhaustive: never = area
      throw new Error(`Unknown snap area: ${_exhaustive}`)
    }
  }
}
